<template>
    <div class="menu user_info_border">
        <div class="dropdown_list_header">
            <div class="avatar_label_group">
                <div class="text_and_supporting_text">
                    <div class="text">{{ user.first_name }} {{ user.last_name }} </div>
                    <div class="supporting-text">{{ user.username }}</div>
                    <div v-if="user.role" class="role-text">{{ roleLabel(user.role) }}</div>
                </div>
            </div>
        </div>
        <div class="menu-items">
            <div class="dropdown-list-item" v-if="user.is_superuser">
                <div class="content">
                    <div class="icon-and-text" @click="modalUsersManager">
                        <Avatar class="user-01-icon" />
                        <div class="text1">Gestión de Usuarios</div>
                    </div>
                </div>
            </div>
            <div class="dropdown-list-item">
                <div class="content">
                    <div class="icon-and-text" @click="modalRequestCompany">
                        <Layers class="user-01-icon" />
                        <div class="text1">Solicitar otra empresa</div>
                    </div>
                </div>
            </div>
            <div v-if="showAnalystPanelButton" class="dropdown-list-item">
                <div class="content">
                    <div class="icon-and-text" @click="goToAnalystPanel">
                        <DashboardSquare class="user-01-icon" />
                        <div class="text1">Panel del analista</div>
                    </div>
                </div>
            </div>
            <div v-if="myRequests.length" class="requests-section">
                <div class="requests-title">Mis solicitudes</div>
                <div
                    v-for="request in myRequests"
                    :key="request.id"
                    class="request-row"
                    :class="statusClass(request.status)"
                >
                    <span class="request-dot" aria-hidden="true"></span>
                    <div class="request-info">
                        <div class="request-name">{{ request.requested_name }}</div>
                        <div class="request-status">{{ statusLabel(request) }}</div>
                    </div>
                    <button
                        v-if="canDismiss(request.status)"
                        class="request-dismiss"
                        aria-label="Eliminar notificación"
                        @click.stop="dismissRequest(request.id)"
                    >
                        <Trash class="dismiss-icon" />
                    </button>
                </div>
            </div>
            <div class="dropdown-list-item1">
                <img class="divider-icon" alt="" src="Divider.svg">
            </div>
            <div class="dropdown-list-item">
                <div class="content">
                    <div class="icon-and-text" @click="logout">
                        <Logout class="user-01-icon" />
                        <div class="text1">Cerrar sesión</div>
                    </div>
                </div>
            </div>
        </div>
    </div>

</template>

<script>
import UsersManager from "@/components/modals/UsersManager";
import RequestCompanyModal from "@/components/modals/RequestCompanyModal";
import Avatar from "@/components/svg/Avatar.vue";
import Logout from "@/components/svg/Logout.vue";
import Layers from "@/components/svg/Layers.vue";
import Trash from "@/components/svg/Trash.vue";
import DashboardSquare from "@/components/svg/DashboardSquare.vue";
import { roleLabel } from "@/utils/roles";
import { reloadToLogin } from "@/utils/auth";
import { mapActions, mapState } from "vuex";

const STATUS_LABELS = {
    pending: "Pendiente de revisión",
    approved: "Aceptada",
    rejected: "Rechazada",
    cancelled: "Cancelada",
};

export default {
    name: "UserMenu",
    components: {
        Avatar,
        Logout,
        Layers,
        Trash,
        DashboardSquare,
    },
    props: {
        user: Object,
    },
    computed: {
        ...mapState("companyLinkRequests", ["myRequests"]),
        showAnalystPanelButton() {
            // Solo analistas, y solo si NO estamos ya en el panel del analista.
            return (
                this.user.user_type === "ANALISTA" &&
                this.$route &&
                this.$route.name !== "panel-analista"
            );
        },
    },
    created() {
        this.fetchMyRequests();
    },
    methods: {
        roleLabel,
        ...mapActions("userAuth", [
            "clearUserInfo",
            "setLoginInfo",
            "deleteSelf",
            "getUserInfo",
        ]),
        ...mapActions("companyLinkRequests", [
            "fetchMyRequests",
            "dismissRequest",
        ]),
        statusLabel(request) {
            if (request.status === "rejected" && request.reason) {
                return `Rechazada: ${request.reason}`;
            }
            return STATUS_LABELS[request.status] || request.status;
        },
        statusClass(status) {
            return `is-${status}`;
        },
        canDismiss(status) {
            // Resueltas (aprobada/rechazada) se pueden ocultar; pendientes no.
            return status === "approved" || status === "rejected";
        },
        goToAnalystPanel() {
            this.$router.push({ name: "panel-analista" }).catch(() => {});
        },
        modalRequestCompany() {
            // parent: this es imprescindible — Buefy crea el modal como instancia
            // fuera del árbol; sin parent no hereda $store/$router y el dispatch
            // de Vuex revienta.
            this.$buefy.modal.open({
                parent: this,
                component: RequestCompanyModal,
                hasModalCard: true,
                trapFocus: true,
                canCancel: ["escape", "outside"],
            });
        },
        modalUsersManager() {
            this.$buefy.modal.open({
                parent: this,
                component: UsersManager,
                hasModalCard: true,
                trapFocus: true,
                canCancel: ["escape", "outside"],
            });
        },
        async logout() {
            // El store ya no navega; el caller recarga tras limpiar la sesión
            // (recarga completa: vacía todos los módulos de Vuex).
            try {
                await this.clearUserInfo();
            } finally {
                reloadToLogin();
            }
        },
    },
};
</script>
<style scoped lang="scss">
.text {
    align-self: stretch;
    position: relative;
    line-height: 20px;
    font-weight: 600;
}

.supporting-text {
    align-self: stretch;
    position: relative;
    line-height: 20px;
    color: #475467;
    word-wrap: break-word;
    word-break: break-all;
    overflow-wrap: break-word;
    white-space: normal;
}

.role-text {
    align-self: stretch;
    position: relative;
    margin-top: 2px;
    line-height: 18px;
    font-size: 12px;
    font-weight: 500;
    color: #0d5154;
}


.user-01-icon {
    width: 16px;
    position: relative;
    height: 16px;
    overflow: hidden;
    flex-shrink: 0;
}

.text1 {
    flex: 1;
    position: relative;
    line-height: 20px;
    font-weight: 500;
}

.icon-and-text {
    flex: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    cursor: pointer;
}

.content {
    flex: 1;
    border-radius: 6px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 9px 10px;
    transition: background-color var(--app-transition-fast, 0.15s ease);
}

.content:hover {
    background-color: var(--Gray-50, #f9fafb);
}

.dropdown-list-item {
    align-self: stretch;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 1px 6px;
}

.divider-icon {
    flex: 1;
    position: relative;
    max-width: 100%;
    overflow: hidden;
    height: 1px;
}

.dropdown-list-item1 {
    width: 240px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    padding: 4px 0px;
    box-sizing: border-box;
}

.requests-section {
    align-self: stretch;
    padding: 4px 8px 8px;
}

.requests-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--report-text-muted-soft, #9ca3af);
    padding: 6px 8px 4px;
}

.request-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 10px;
    border-radius: 8px;
    border: 1px solid var(--Gray-200, #eaecf0);
    background-color: var(--Gray-50, #f9fafb);
    margin-bottom: 6px;
}

/* Estado por punto de color: neutro por defecto, semántico al resolverse.
   Whisper-quiet — el nombre es el énfasis, el dot sólo orienta al vistazo. */
.request-dot {
    flex-shrink: 0;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: var(--report-text-muted-soft, #9ca3af);
}

.is-approved .request-dot {
    background-color: var(--report-positive, #16a34a);
}

.is-rejected .request-dot {
    background-color: var(--report-negative, #dc2626);
}

.request-info {
    min-width: 0;
    flex: 1;
}

.request-name {
    font-size: 13px;
    font-weight: 600;
    color: var(--Gray-700, #344054);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.request-status {
    font-size: 11px;
    color: var(--report-text-muted, #6b7280);

    .is-rejected & {
        color: var(--report-negative, #dc2626);
    }

    .is-approved & {
        color: var(--report-positive, #16a34a);
    }
}

.request-dismiss {
    flex-shrink: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    transition: background-color var(--app-transition-fast, 0.15s ease);
}

.request-dismiss:hover {
    background-color: var(--report-negative-bg, #fef2f2);
}

.dismiss-icon {
    width: 14px;
    height: 14px;
    color: var(--report-text-muted-soft, #9ca3af);
    transition: color var(--app-transition-fast, 0.15s ease);
}

.request-dismiss:hover .dismiss-icon {
    color: var(--report-negative, #dc2626);
}

.menu-items {
    align-self: stretch;
    background-color: #fff;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 4px 0px;
}

.menu {
    position: relative;
    box-shadow: 0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03);
    border-radius: 8px;
    background-color: #fff;
    border: 1px solid #eaecf0;
    box-sizing: border-box;
    width: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    text-align: left;
    font-size: 14px;
    color: #344054;
    font-family: Inter;
}

.avatar_icon {
    width: 100%;
    position: relative;
    border-radius: 200px;
    height: 24px;
    z-index: 2;
}

.user_info_border {
    border: 1px solid #EAECF0;
    border-radius: 10px;
}

.dropdown_list_header {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    padding: 12px 16px;
}

.avatar_label_group {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
}

.text_and_supporting_text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    flex: 1;
}

.extra_margin {
    margin-right: 200px;
}
</style>
